import { useMemo, useState, type ChangeEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  DataTable,
  EstadoVazio,
  Modal,
  Pagination,
  Pill,
  TextField,
  TextareaField,
  type Column,
  fmtBRL,
} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { useFiltros } from "../../lib/use-filtros";
import type {
  AdminPrefeitura,
  BloqueioMargem,
  CategoriaFolhaMargem,
  CompromissoMargem,
  ConfiguracaoMargem,
  ConfiguracaoMargemInput,
  DivergenciaMargem,
  FotoMargemImportacao,
  ImpedimentoAtivacaoMargem,
  ImportacaoConsignacaoInput,
  PaginaMargem,
} from "@atlas/sdk";

type Painel = "configuracoes" | "fotos" | "compromissos" | "bloqueios" | "divergencias";
const POR_PAGINA = 10;

async function lerArquivoTexto(file: File): Promise<string> {
  const ler = (encoding: string) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Não foi possível ler o arquivo."));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file, encoding);
  });
  const utf8 = await ler("utf-8");
  const texto = utf8.includes("�") ? await ler("windows-1252") : utf8;
  return texto.replace(/^\uFEFF/, "");
}

function normalizarCompetenciaImportacao(valor: string): string | null {
  const digitos = valor.replace(/\D/g, "");
  const competencia = /^\d{4}$/.test(digitos) ? `20${digitos}` : digitos;
  if (!/^\d{6}$/.test(competencia)) return null;
  const mes = Number(competencia.slice(4, 6));
  return mes >= 1 && mes <= 12 ? competencia : null;
}

const baseAlece = (unidadeId: number): ConfiguracaoMargemInput => ({
  unidadeId,
  competenciaInicio: new Date().toISOString().slice(0, 7).replace("-", ""),
  competenciaFim: null,
  modalidade: "informada",
  escopoSaldo: "compartilhado",
  permiteEmprestimo: true,
  permiteCartaoConsignado: true,
  permiteCartaoBeneficio: true,
  validadeCompetencias: 1,
  toleranciaDias: 0,
  // Decidido em 19/09/2026 para a ALECE: arquivo atrasado segue pelo ultimo
  // saldo informado, com alerta (ela aceitou isso na resposta 10).
  politicaAtraso: "seguir_com_alerta",
  tetoParcelas: 120,
  categoriasFolha: [],
  bloqueioInformado: "bloqueia_tudo",
  permitirReducaoSemSaldo: true,
  aceitaRetificador: false,
  movimentoGranularidade: "agregado",
  tombaSemSalario: false,
  consignatariaSemArquivo: "repetir_recorrentes",
  fundamento: "",
  justificativa: "",
});

function estado(c: ConfiguracaoMargem) {
  if (!c.ativa) return { texto: "Inativa", variante: "expirado" as const };
  return { texto: "Ativa", variante: "aceita" as const };
}

/** A lista do 422 `ativacao_impedida`; vazia para qualquer outro erro. */
function impedimentosDoErro(e: unknown): ImpedimentoAtivacaoMargem[] {
  const x = e as { code?: string; details?: { impedimentos?: unknown } } | null;
  return x?.code === "ativacao_impedida" && Array.isArray(x.details?.impedimentos)
    ? (x.details.impedimentos as ImpedimentoAtivacaoMargem[])
    : [];
}

/**
 * A margem pelo arquivo do órgão tem dois estados: inativa (valem as regras
 * percentuais) e ativa (o saldo informado decide). Ativar recusa e lista o que
 * falta; não há modo sombra, ensaio nem liberação prévia (29/09/2026).
 */
export function MargemInformadaPainel({ org }: { org: AdminPrefeitura }) {
  const qc = useQueryClient();
  const chave = ["admin", "margem-informada", org.id] as const;
  const resumo = useQuery({
    queryKey: [...chave, "resumo"],
    queryFn: () => atlas.admin.margemInformada.resumo(org.id),
  });
  const [painel, setPainel] = useState<Painel>("configuracoes");
  const [pagina, setPagina] = useState(1);
  const [editando, setEditando] = useState<ConfiguracaoMargem | "nova" | null>(null);
  const [acao, setAcao] = useState<{
    tipo: "ativar" | "desativar";
    cfg: ConfiguracaoMargem;
  } | null>(null);
  const [falha, setFalha] = useState<{ mensagem: string; impedimentos: ImpedimentoAtivacaoMargem[] } | null>(null);
  const ativa = resumo.data?.configuracaoAtiva ?? null;

  const atualizar = () => void qc.invalidateQueries({ queryKey: chave });
  const executar = useMutation({
    mutationFn: async () => {
      if (!acao) return;
      return acao.tipo === "ativar"
        ? atlas.admin.margemInformada.ativar(acao.cfg.id)
        : atlas.admin.margemInformada.desativar(acao.cfg.id);
    },
    onSuccess: () => {
      setAcao(null);
      setFalha(null);
      atualizar();
    },
    onError: (e) => setFalha({ mensagem: mensagemDeErro(e), impedimentos: impedimentosDoErro(e) }),
  });
  const fecharAcao = () => {
    setAcao(null);
    setFalha(null);
  };

  const mudarPainel = (p: Painel) => {
    setPainel(p);
    setPagina(1);
  };
  const botoes: { key: Painel; label: string }[] = [
    { key: "configuracoes", label: "Configurações" },
    { key: "fotos", label: "Fotos e importações" },
    { key: "compromissos", label: "Compromissos" },
    { key: "bloqueios", label: "Bloqueios" },
    { key: "divergencias", label: "Divergências" },
  ];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card aria-busy={resumo.isLoading ? "true" : undefined}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16 }}>Estratégia de autorização</h2>
            <p style={{ margin: "5px 0 0", color: "var(--text-muted)", fontSize: 13 }}>
              Define de onde vem o saldo usado por consignatária e servidor.
            </p>
          </div>
          <Button onClick={() => setEditando("nova")}>Nova configuração</Button>
        </div>
        {resumo.isError ? (
          <Aviso erro>{mensagemDeErro(resumo.error)}</Aviso>
        ) : ativa ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))",
              gap: 14,
              marginTop: 16,
            }}
          >
            <Info rotulo="Modalidade">
              {ativa.modalidade === "informada" ? "Saldo informado" : "Calculada"}
            </Info>
            <Info rotulo="Estado">
              <Pill variant={estado(ativa).variante}>{estado(ativa).texto}</Pill>
            </Info>
            <Info rotulo="Vigência">
              {ativa.competenciaInicio} — {ativa.competenciaFim ?? "sem fim"}
            </Info>
            <Info rotulo="Última foto">{resumo.data?.fotoCompetencia ?? "Nenhuma publicada"}</Info>
            <Info rotulo="Validade">{ativa.validadeCompetencias} competência(s)</Info>
            <Info rotulo="Produtos">
              {[
                ativa.permiteEmprestimo && "Empréstimo",
                ativa.permiteCartaoConsignado && "Cartão",
                ativa.permiteCartaoBeneficio && "Benefício",
              ]
                .filter(Boolean)
                .join(", ")}
            </Info>
          </div>
        ) : (
          <EstadoVazio
            titulo="Estratégia calculada"
            orientacao="Nenhuma configuração de margem informada está ativa. Valem as regras percentuais abaixo."
          />
        )}
        {resumo.data && (resumo.data.saldosAusentes > 0 || resumo.data.bloqueios > 0) ? (
          <Aviso>
            {resumo.data.saldosAusentes} saldo(s) ausente(s) · {resumo.data.bloqueios} bloqueio(s) ativo(s)
          </Aviso>
        ) : null}
      </Card>

      <div
        role="tablist"
        aria-label="Operação da margem"
        style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
      >
        {botoes.map((b) => (
          <Button
            key={b.key}
            size="sm"
            variant={painel === b.key ? "primary" : "ghost"}
            onClick={() => mudarPainel(b.key)}
          >
            {b.label}
          </Button>
        ))}
      </div>

      {painel === "configuracoes" ? (
        <Configuracoes
          configs={resumo.data?.configuracoes ?? []}
          loading={resumo.isLoading}
          onEditar={setEditando}
          onAcao={(tipo, cfg) => {
            setFalha(null);
            setAcao({ tipo, cfg });
          }}
        />
      ) : null}
      {painel !== "configuracoes" ? (
        <OperacaoTabela painel={painel} unidadeId={org.id} pagina={pagina} onPagina={setPagina} />
      ) : null}

      <ConfiguracaoModal
        key={editando === "nova" ? "nova" : (editando?.id ?? "fechado")}
        org={org}
        valor={editando}
        onClose={() => setEditando(null)}
        onSaved={() => {
          setEditando(null);
          atualizar();
        }}
      />
      <Modal
        open={acao != null}
        onClose={fecharAcao}
        title={acao?.tipo === "ativar" ? "Ativar configuração" : "Desativar configuração"}
        danger={acao?.tipo === "desativar"}
        footer={
          <>
            <Button variant="ghost" onClick={fecharAcao}>
              Cancelar
            </Button>
            <Button onClick={() => executar.mutate()} disabled={executar.isPending}>
              {executar.isPending ? "Aplicando…" : "Confirmar"}
            </Button>
          </>
        }
      >
        {acao ? (
          <div style={{ display: "grid", gap: 10, fontSize: 13 }}>
            <p style={{ margin: 0 }}>
              <strong>{org.nome}</strong> · configuração #{acao.cfg.id} · vigência desde{" "}
              {acao.cfg.competenciaInicio}.
            </p>
            <p style={{ margin: 0, color: "var(--text-muted)" }}>
              {acao.tipo === "ativar"
                ? "O saldo informado pelo órgão passa a decidir as autorizações em todos os canais."
                : "A unidade volta à estratégia calculada; fotos e compromissos são preservados."}
            </p>
            {falha ? (
              <Aviso erro>
                {falha.impedimentos.length ? (
                  <>
                    <strong>Não foi possível ativar. Corrija antes:</strong>
                    <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                      {falha.impedimentos.map((i) => (
                        <li key={i.codigo}>
                          {i.mensagem}
                          {i.itens?.length
                            ? ` (${i.itens.slice(0, 5).join(", ")}${i.itens.length > 5 ? "…" : ""})`
                            : ""}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  falha.mensagem
                )}
              </Aviso>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function Configuracoes({
  configs,
  loading,
  onEditar,
  onAcao,
}: {
  configs: ConfiguracaoMargem[];
  loading: boolean;
  onEditar: (c: ConfiguracaoMargem) => void;
  onAcao: (t: "ativar" | "desativar", c: ConfiguracaoMargem) => void;
}) {
  const colunas: Column<ConfiguracaoMargem>[] = [
    {
      key: "vigencia",
      header: "Vigência",
      mono: true,
      render: (c) => `${c.competenciaInicio} — ${c.competenciaFim ?? "…"}`,
    },
    {
      key: "modalidade",
      header: "Fonte",
      render: (c) => (c.modalidade === "informada" ? "Saldo informado" : "Calculada"),
    },
    {
      key: "estado",
      header: "Estado",
      render: (c) => <Pill variant={estado(c).variante}>{estado(c).texto}</Pill>,
    },
    { key: "foto", header: "Validade", render: (c) => `${c.validadeCompetencias} competência(s)` },
    {
      key: "fundamento",
      header: "Fundamento",
      wrap: true,
      render: (c) => (
        <div>
          <div>{c.fundamento}</div>
          <small style={{ color: "var(--text-dim)" }}>{c.justificativa}</small>
        </div>
      ),
    },
  ];
  return (
    <Card>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>Histórico de configurações</h2>
      </div>
      <DataTable
        columns={colunas}
        rows={configs}
        rowKey={(c) => String(c.id)}
        loading={loading}
        emptyState="Nenhuma configuração cadastrada."
        actions={(c) => (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {!c.ativadaEm ? (
              <Button size="sm" variant="ghost" onClick={() => onEditar(c)}>
                Editar
              </Button>
            ) : null}
            {c.ativa ? (
              <Button size="sm" variant="ghost" onClick={() => onAcao("desativar", c)}>
                Desativar
              </Button>
            ) : (
              <Button size="sm" onClick={() => onAcao("ativar", c)}>
                Ativar
              </Button>
            )}
          </div>
        )}
      />
    </Card>
  );
}

function ConfiguracaoModal({
  org,
  valor,
  onClose,
  onSaved,
}: {
  org: AdminPrefeitura;
  valor: ConfiguracaoMargem | "nova" | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const inicial = useMemo(
    () => (valor && valor !== "nova" ? { ...valor } : baseAlece(org.id)),
    [valor, org.id],
  );
  const [form, setForm] = useState<ConfiguracaoMargemInput>(inicial);
  const [erro, setErro] = useState<string | null>(null);
  const salvar = useMutation({
    mutationFn: () =>
      valor !== "nova" && valor
        ? atlas.admin.margemInformada.corrigir(valor.id, form)
        : atlas.admin.margemInformada.criar(form),
    onSuccess: onSaved,
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  if (!valor) return null;
  const valido =
    /^\d{6}$/.test(form.competenciaInicio) &&
    (!form.competenciaFim || /^\d{6}$/.test(form.competenciaFim)) &&
    form.fundamento.trim().length >= 3 &&
    form.justificativa.trim().length >= 3 &&
    form.tetoParcelas >= 1 && form.tetoParcelas <= 120 &&
    (form.categoriasFolha ?? []).every(
      (c) => c.folha.trim() && c.categoria.trim() && c.fundamento.trim().length >= 3
        && (c.prazoMaxMeses == null || c.prazoMaxMeses >= 1),
    );
  const set =<K extends keyof ConfiguracaoMargemInput>(k: K, v: ConfiguracaoMargemInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  return (
    <Modal
      open
      title={valor === "nova" ? "Nova configuração de margem informada" : "Corrigir configuração inativa"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => salvar.mutate()} disabled={!valido || salvar.isPending}>
            {salvar.isPending ? "Salvando…" : "Salvar configuração"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {erro ? <Aviso erro>{erro}</Aviso> : null}
        <Aviso>
          Modalidade informada e saldo compartilhado. A configuração nasce inativa: ela só passa a decidir
          quando você clicar em Ativar. Opções ainda não executadas pelo motor não são oferecidas.
        </Aviso>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <TextField
            label="Competência inicial"
            value={form.competenciaInicio}
            onChange={(e) => set("competenciaInicio", e.target.value)}
            hint="AAAAMM"
          />
          <TextField
            label="Competência final"
            value={form.competenciaFim ?? ""}
            onChange={(e) => set("competenciaFim", e.target.value || null)}
            hint="Opcional"
          />
        </div>
        <TextField
          label="Validade da foto (competências)"
          type="number"
          value={String(form.validadeCompetencias)}
          onChange={(e) => set("validadeCompetencias", Math.max(0, Number(e.target.value)))}
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <TextField
            label="Tolerância após a validade (dias)"
            type="number"
            value={String(form.toleranciaDias)}
            onChange={(e) => set("toleranciaDias", Math.max(0, Number(e.target.value)))}
          />
          <TextField
            label="Teto de parcelas"
            type="number"
            value={String(form.tetoParcelas)}
            onChange={(e) => set("tetoParcelas", Math.min(120, Math.max(1, Number(e.target.value))))}
            hint="Máximo 120"
          />
        </div>
        <Check
          label="Arquivo atrasado: seguir pelo último saldo informado, com alerta"
          checked={form.politicaAtraso === "seguir_com_alerta"}
          onChange={(v) => set("politicaAtraso", v ? "seguir_com_alerta" : "recusar_aumento")}
        />
        <CategoriasFolhaEditor
          valor={form.categoriasFolha ?? []}
          onChange={(v) => set("categoriasFolha", v)}
        />
        <fieldset style={{ border: "1px solid var(--border)", borderRadius: 8, display: "grid", gap: 8 }}>
          <legend>Produtos habilitados</legend>
          <Check
            label="Empréstimo"
            checked={form.permiteEmprestimo}
            onChange={(v) => set("permiteEmprestimo", v)}
          />
          <Check
            label="Cartão consignado"
            checked={form.permiteCartaoConsignado}
            onChange={(v) => set("permiteCartaoConsignado", v)}
          />
          <Check
            label="Cartão benefício"
            checked={form.permiteCartaoBeneficio}
            onChange={(v) => set("permiteCartaoBeneficio", v)}
          />
        </fieldset>
        <fieldset style={{ border: "1px solid var(--border)", borderRadius: 8, display: "grid", gap: 8 }}>
          <legend>Políticas ALECE</legend>
          <Check
            label="Permitir redução com origem válida mesmo sem saldo"
            checked={form.permitirReducaoSemSaldo}
            onChange={(v) => set("permitirReducaoSemSaldo", v)}
          />
          <Check
            label="Aceitar retificador na mesma competência"
            checked={form.aceitaRetificador}
            onChange={(v) => set("aceitaRetificador", v)}
          />
          <Check
            label="Registrar a dívida de servidor com salário zero ou negativo (ele continua no movimento)"
            checked={form.tombaSemSalario}
            onChange={(v) => set("tombaSemSalario", v)}
          />
          <Check
            label="Sem o arquivo mensal da consignatária até o corte, bloquear a emissão (desmarcado: repete as recorrentes e as com prazo, sem as avulsas, com alerta)"
            checked={form.consignatariaSemArquivo === "bloquear"}
            onChange={(v) => set("consignatariaSemArquivo", v ? "bloquear" : "repetir_recorrentes")}
          />
        </fieldset>
        <TextField
          label="Fundamento"
          value={form.fundamento}
          onChange={(e) => set("fundamento", e.target.value)}
        />
        <TextareaField
          label="Justificativa"
          value={form.justificativa}
          onChange={(e) => set("justificativa", e.target.value)}
          rows={3}
        />
      </div>
    </Modal>
  );
}

function OperacaoTabela({
  painel,
  unidadeId,
  pagina,
  onPagina,
}: {
  painel: Exclude<Painel, "configuracoes">;
  unidadeId: number;
  pagina: number;
  onPagina: (p: number) => void;
}) {
  const offset = (pagina - 1) * POR_PAGINA;
  const [matricula, setMatricula] = useState("");
  const [folha, setFolha] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [produto, setProduto] = useState("");
  // As dimensoes que cada aba filtra DE FATO. O `check:grids` compara estes
  // `nome` com o inventario (grids-inventario.ts): dimensao declarada e nao
  // filtravel, ou filtravel e nao declarada, derruba o CI.
  const filtrosGrid = useFiltros(
    painel === "fotos"
      ? [
          { nome: "competencia", ligado: matricula !== "", limpar: () => setMatricula("") },
          { nome: "situacao", ligado: estadoFiltro !== "", limpar: () => setEstadoFiltro("") },
        ]
      : [
          ...(painel !== "divergencias"
            ? [
                { nome: "matricula", ligado: matricula !== "", limpar: () => setMatricula("") },
                { nome: "folha", ligado: folha !== "", limpar: () => setFolha("") },
              ]
            : []),
          ...(painel === "compromissos" ? [{ nome: "produto", ligado: produto !== "", limpar: () => setProduto("") }] : []),
          ...(painel === "divergencias" ? [{ nome: "tipo", ligado: produto !== "", limpar: () => setProduto("") }] : []),
          { nome: "estado", ligado: estadoFiltro !== "", limpar: () => setEstadoFiltro("") },
        ],
    onPagina,
  );
  const alterar = (setter: (v: string) => void) => (e: ChangeEvent<HTMLInputElement>) =>
    filtrosGrid.filtrar(setter)(e.target.value);
  const q = useQuery<PaginaMargem<unknown>>({
    queryKey: [
      "admin",
      "margem-informada",
      unidadeId,
      painel,
      pagina,
      matricula,
      folha,
      estadoFiltro,
      produto,
    ],
    queryFn: async () => {
      const r =
        painel === "fotos"
          ? await atlas.admin.margemInformada.importacoes({
              unidadeId,
              competencia: matricula || undefined,
              status: estadoFiltro || undefined,
              limit: POR_PAGINA,
              offset,
            })
          : painel === "compromissos"
            ? await atlas.admin.margemInformada.compromissos({
                unidadeId,
                matricula: matricula || undefined,
                folha: folha || undefined,
                produto: produto || undefined,
                estado: estadoFiltro || undefined,
                limit: POR_PAGINA,
                offset,
              })
            : painel === "bloqueios"
              ? await atlas.admin.margemInformada.bloqueios({
                  unidadeId,
                  matricula: matricula || undefined,
                  folha: folha || undefined,
                  ativa: estadoFiltro === "ativo" ? true : estadoFiltro === "encerrado" ? false : undefined,
                  limit: POR_PAGINA,
                  offset,
                })
              : await atlas.admin.margemInformada.divergencias({
                  unidadeId, estado: estadoFiltro || undefined, tipo: produto || undefined,
                  limit: POR_PAGINA, offset,
                });
      return r as PaginaMargem<unknown>;
    },
    placeholderData: keepPreviousData,
  });
  const filtros = (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))",
          gap: 8,
          marginBottom: 12,
        }}
      >
        {painel !== "divergencias" ? <TextField
          label={painel === "fotos" ? "Competência" : "Matrícula"}
          value={matricula}
          onChange={alterar(setMatricula)}
        /> : null}
        {painel !== "fotos" && painel !== "divergencias" ? <TextField label="Folha" value={folha} onChange={alterar(setFolha)} /> : null}
        {painel === "compromissos" || painel === "divergencias" ? (
          <TextField label={painel === "divergencias" ? "Tipo" : "Produto"} value={produto} onChange={alterar(setProduto)} />
        ) : null}
        <TextField
          label={painel === "fotos" ? "Situação" : "Estado"}
          value={estadoFiltro}
          onChange={alterar(setEstadoFiltro)}
          hint={painel === "bloqueios" ? "ativo ou encerrado" : painel === "divergencias" ? "aberta, explicada ou resolvida" : undefined}
        />
        {filtrosGrid.temFiltro ? (
          <div style={{ alignSelf: "end" }}>
            <Button size="sm" variant="ghost" onClick={filtrosGrid.limparFiltros}>
              {filtrosGrid.propsFilterBar.resetLabel}
            </Button>
          </div>
        ) : null}
      </div>
    );
  if (painel === "fotos")
    return (
      <ListaFotos
        q={q as never}
        unidadeId={unidadeId}
        pagina={pagina}
        onPagina={onPagina}
        filtros={filtros}
      />
    );
  if (painel === "compromissos")
    return (
      <ListaGenerica
        titulo="Compromissos"
        q={q as never}
        pagina={pagina}
        onPagina={onPagina}
        colunas={colunasCompromissos}
        filtros={filtros}
      />
    );
  if (painel === "bloqueios")
    return (
      <ListaGenerica
        titulo="Bloqueios"
        q={q as never}
        pagina={pagina}
        onPagina={onPagina}
        colunas={colunasBloqueios}
        filtros={filtros}
      />
    );
  return (
    <ListaGenerica
      titulo="Divergências e conciliação"
      q={q as never}
      pagina={pagina}
      onPagina={onPagina}
      colunas={colunasDivergencias}
      filtros={filtros}
      actions={(x) => <ResolverDivergenciaButton divergencia={x} unidadeId={unidadeId} />}
    />
  );
}

type Resultado<T> = {
  data?: { itens: T[]; total: number };
  isLoading: boolean;
  isError: boolean;
  error: unknown;
};
function ListaGenerica<T>({
  titulo,
  q,
  pagina,
  onPagina,
  colunas,
  filtros,
  actions,
}: {
  titulo: string;
  q: Resultado<T>;
  pagina: number;
  onPagina: (p: number) => void;
  colunas: Column<T>[];
  filtros?: ReactNode;
  actions?: (row: T) => ReactNode;
}) {
  return (
    <Card>
      <h2 style={{ margin: "0 0 12px", fontSize: 16 }}>{titulo}</h2>
      {filtros}
      {q.isError ? (
        <Aviso erro>{mensagemDeErro(q.error)}</Aviso>
      ) : (
        <>
          <DataTable
            columns={colunas}
            rows={q.data?.itens ?? []}
            rowKey={(x) => String((x as { id: string }).id)}
            loading={q.isLoading}
            emptyState={`Nenhum registro em ${titulo.toLowerCase()}.`}
            actions={actions}
          />
          <Pagination
            total={q.data?.total ?? 0}
            pagina={pagina}
            porPagina={POR_PAGINA}
            onPagina={onPagina}
            onPorPagina={() => undefined}
            rotulo={titulo.toLowerCase()}
          />
        </>
      )}
    </Card>
  );
}

function ResolverDivergenciaButton({ divergencia, unidadeId }: { divergencia: DivergenciaMargem; unidadeId: number }) {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [justificativa, setJustificativa] = useState("");
  const [estado, setEstado] = useState<"explicada" | "resolvida">("resolvida");
  const salvar = useMutation({
    mutationFn: () => atlas.admin.margemInformada.resolverDivergencia(divergencia.id, { unidadeId, estado, justificativa }),
    onSuccess: async () => {
      setAberto(false); setJustificativa("");
      await qc.invalidateQueries({ queryKey: ["admin", "margem-informada", unidadeId, "divergencias"] });
    },
  });
  if (divergencia.estado && divergencia.estado !== "aberta") return <span>{divergencia.estado}</span>;
  return <>
    <Button size="sm" variant="ghost" onClick={() => setAberto(true)}>Revisar</Button>
    <Modal open={aberto} title="Revisar divergência" onClose={() => setAberto(false)} footer={<>
      <Button variant="ghost" onClick={() => setAberto(false)}>Cancelar</Button>
      <Button disabled={justificativa.trim().length < 10 || salvar.isPending} onClick={() => salvar.mutate()}>Salvar revisão</Button>
    </>}>
      <div style={{ display: "grid", gap: 12 }}>
        <p style={{ margin: 0 }}>{divergencia.detalhes}</p>
        <label style={{ display: "grid", gap: 4, fontSize: 13 }}>Resultado
          <select value={estado} onChange={(e) => setEstado(e.target.value as "explicada" | "resolvida")}>
            <option value="resolvida">Resolvida</option>
            <option value="explicada">Explicada</option>
          </select>
        </label>
        <TextareaField label="Justificativa" rows={4} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} />
        {salvar.isError ? <Aviso erro>{mensagemDeErro(salvar.error)}</Aviso> : null}
      </div>
    </Modal>
  </>;
}
function ListaFotos({
  q,
  unidadeId,
  pagina,
  onPagina,
  filtros,
}: {
  q: Resultado<FotoMargemImportacao>;
  unidadeId: number;
  pagina: number;
  onPagina: (p: number) => void;
  filtros?: ReactNode;
}) {
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>Fotos e importações</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <ImportarParteButton unidadeId={unidadeId} />
          <ImportarConsignacaoButton unidadeId={unidadeId} />
          <Link to={`/averbadora/servidores/importar?prefeituraId=${unidadeId}&formato=carga_inicial`}>
            <Button size="sm" variant="ghost">Carga inicial</Button>
          </Link>
        </div>
      </div>
      {filtros}
      <DataTable
        columns={colunasFotos}
        rows={q.data?.itens ?? []}
        rowKey={(x) => x.id}
        loading={q.isLoading}
        emptyState="Nenhuma foto importada."
      />
      <Pagination
        total={q.data?.total ?? 0}
        pagina={pagina}
        porPagina={POR_PAGINA}
        onPagina={onPagina}
        onPorPagina={() => undefined}
        rotulo="importações"
      />
    </Card>
  );
}

const ROTULO_COMPORTAMENTO: Record<"recorrente" | "prazo" | "avulsa", string> = {
  recorrente: "recorrente",
  prazo: "com prazo",
  avulsa: "avulsa",
};

function ImportarConsignacaoButton({ unidadeId }: { unidadeId: number }) {
  const [aberto, setAberto] = useState(false);
  // Sem `comportamento`: quem decide e' a declaracao da rubrica (#165). Sem
  // credor = consignacao sem credor (ADR-0010), como a 631 MONGERAL.
  const [form, setForm] = useState<ImportacaoConsignacaoInput>({ unidadeId, credorId: null, competencia: "", rubrica: "", modo: "completo", arquivo: "", conteudo: "" });
  const [previa, setPrevia] = useState<{ hash: string; linhas: number; total: number } | null>(null);
  const alterar = <K extends keyof ImportacaoConsignacaoInput>(k: K, v: ImportacaoConsignacaoInput[K]) => { setForm((f) => ({ ...f, [k]: v })); setPrevia(null); };
  const ensaiar = useMutation({ mutationFn: () => atlas.admin.margemInformada.preverConsignacoes(form), onSuccess: (r) => setPrevia({ hash: r.previaHash, linhas: r.previa.linhas, total: r.previa.totalCentavos }) });
  const confirmar = useMutation({ mutationFn: () => atlas.admin.margemInformada.confirmarConsignacoes(form, previa!.hash), onSuccess: () => { setAberto(false); setPrevia(null); } });
  const competenciaOk = /^\d{6}$/.test(form.competencia);
  const rubricaNormal = form.rubrica.trim().replace(/^0+(?=\d)/, "");
  const declarados = useQuery({
    queryKey: ["admin", "rubricas", "comportamentos", unidadeId, form.competencia],
    queryFn: () => atlas.admin.rubricas.comportamentos(unidadeId, form.competencia),
    enabled: aberto && competenciaOk,
  });
  const declarado = rubricaNormal
    ? declarados.data?.comportamentos.find((d) => d.rubrica === rubricaNormal)?.comportamento ?? null
    : null;
  const semDeclaracao = form.modo !== "reajuste" && competenciaOk && Boolean(rubricaNormal) && declarados.isSuccess && !declarado;
  const valido = competenciaOk && rubricaNormal && form.arquivo
    && (form.modo === "reajuste" || Boolean(declarado))
    && (declarado !== "prazo" || /^\d{6}$/.test(form.competenciaFim ?? ""))
    && (form.modo === "reajuste" ? Number.isFinite(form.percentual) : Boolean(form.conteudo));
  return <>
    <Button size="sm" variant="ghost" onClick={() => setAberto(true)}>Consignações mensais</Button>
    <Modal open={aberto} title="Importar consignações não creditícias" onClose={() => setAberto(false)} footer={<>
      <Button variant="ghost" onClick={() => setAberto(false)}>Cancelar</Button>
      {!previa ? <Button disabled={!valido || ensaiar.isPending} onClick={() => ensaiar.mutate()}>Gerar prévia</Button> : <Button disabled={confirmar.isPending} onClick={() => confirmar.mutate()}>Confirmar importação</Button>}
    </>}>
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <TextField label="Credor ID" type="number" value={String(form.credorId ?? "")} onChange={(e) => alterar("credorId", e.target.value ? Number(e.target.value) : null)} hint="Vazio = consignação sem credor" />
          <TextField label="Competência" value={form.competencia} onChange={(e) => alterar("competencia", e.target.value)} />
          <TextField label="Rubrica" value={form.rubrica} onChange={(e) => alterar("rubrica", e.target.value)} />
          <label style={{ display: "grid", gap: 4, fontSize: 13 }}>Modo<select value={form.modo} onChange={(e) => alterar("modo", e.target.value as ImportacaoConsignacaoInput["modo"])}><option value="completo">Completo</option><option value="incremental">Incremental</option><option value="reajuste">Reajuste</option></select></label>
          {declarado === "prazo" ? <TextField label="Competência final" value={form.competenciaFim ?? ""} onChange={(e) => alterar("competenciaFim", e.target.value || null)} hint="AAAAMM" /> : null}
        </div>
        {declarado ? <Aviso>Rubrica {rubricaNormal} declarada como {ROTULO_COMPORTAMENTO[declarado]} em {form.competencia}. Para mudar, use o Motor de Rubricas.</Aviso> : null}
        {semDeclaracao ? <Aviso erro>Rubrica {rubricaNormal} sem comportamento declarado em {form.competencia}. Declare no Motor de Rubricas (recorrente, com prazo ou avulsa) antes de importar.</Aviso> : null}
        {form.modo === "reajuste" ? <TextField label="Percentual" type="number" value={String(form.percentual ?? "")} onChange={(e) => alterar("percentual", Number(e.target.value))} /> : <input type="file" accept=".txt,.csv" aria-label="Arquivo da consignatária" onChange={async (e) => { const f=e.target.files?.[0]; if(!f)return; alterar("arquivo",f.name); alterar("conteudo",await lerArquivoTexto(f)); }} />}
        {form.modo === "reajuste" ? <TextField label="Identificação do arquivo" value={form.arquivo} onChange={(e) => alterar("arquivo",e.target.value)} /> : null}
        {previa ? <Aviso>Prévia: {previa.linhas} linha(s), total {fmtBRL(previa.total / 100)}. Confirme sem alterar os dados.</Aviso> : null}
        {ensaiar.isError ? <Aviso erro>{mensagemDeErro(ensaiar.error)}</Aviso> : null}
        {confirmar.isError ? <Aviso erro>{mensagemDeErro(confirmar.error)}</Aviso> : null}
      </div>
    </Modal>
  </>;
}

function ImportarParteButton({ unidadeId }: { unidadeId: number }) {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [competencia, setCompetencia] = useState("");
  const [tipo, setTipo] = useState<"servidor" | "folha">("servidor");
  const [arquivo, setArquivo] = useState("");
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  const competenciaNormalizada = normalizarCompetenciaImportacao(competencia);
  const importar = useMutation({
    mutationFn: async () => {
      if (!competenciaNormalizada || !arquivoSelecionado) {
        throw new Error("Informe uma competência válida e selecione o arquivo.");
      }
      const conteudo = await lerArquivoTexto(arquivoSelecionado);
      if (!conteudo.trim()) throw new Error("O arquivo selecionado está vazio.");
      return atlas.admin.margemInformada.importarParte({
        unidadeId,
        competencia: competenciaNormalizada,
        tipo,
        arquivo,
        conteudo,
      });
    },
    onSuccess: async () => {
      setAberto(false); setArquivo(""); setArquivoSelecionado(null);
      await qc.invalidateQueries({ queryKey: ["admin", "margem-informada", unidadeId] });
    },
  });
  return <>
    <Button size="sm" onClick={() => setAberto(true)}>Importar nova competência</Button>
    <Modal open={aberto} title="Importar parte ALECE" onClose={() => setAberto(false)} footer={<>
      <Button variant="ghost" onClick={() => setAberto(false)}>Cancelar</Button>
      <Button disabled={!competenciaNormalizada || !arquivoSelecionado || importar.isPending} onClick={() => importar.mutate()}>
        {importar.isPending ? "Importando…" : "Importar"}
      </Button>
    </>}>
      <div style={{ display: "grid", gap: 12 }}>
        <Aviso>Envie SERVIDOR e FOLHA da mesma competência em qualquer ordem. A foto só será publicada quando o par estiver completo.</Aviso>
        <TextField
          aria-label="Competência"
          label="Competência"
          hint="AAAAMM ou AAMM, como no nome do arquivo ALECE"
          value={competencia}
          onChange={(e) => setCompetencia(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onBlur={() => {
            const normalizada = normalizarCompetenciaImportacao(competencia);
            if (normalizada) setCompetencia(normalizada);
          }}
        />
        <label style={{ display: "grid", gap: 4, fontSize: 13 }}>Parte
          <select value={tipo} onChange={(e) => setTipo(e.target.value as "servidor" | "folha")}>
            <option value="servidor">SERVIDOR</option><option value="folha">FOLHA</option>
          </select>
        </label>
        <input aria-label="Arquivo SERVIDOR ou FOLHA" type="file" accept=".txt,.csv,text/plain,text/csv" onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          setArquivo(f?.name ?? "");
          setArquivoSelecionado(f);
        }} />
        {importar.isError ? <Aviso erro>{mensagemDeErro(importar.error)}</Aviso> : null}
      </div>
    </Modal>
  </>;
}

const colunasFotos: Column<FotoMargemImportacao>[] = [
  { key: "competencia", header: "Competência", mono: true },
  { key: "versao", header: "Versão" },
  { key: "arquivo", header: "Arquivo", wrap: true },
  {
    key: "status",
    header: "Situação",
    render: (x) => (
      <Pill
        variant={x.status === "publicada" ? "aceita" : x.status === "recusada" ? "rejeitada" : "expirado"}
      >
        {x.status}
      </Pill>
    ),
  },
  { key: "recebida", header: "Recebida em", render: (x) => new Date(x.recebidaEm).toLocaleString("pt-BR") },
];
const colunasCompromissos: Column<CompromissoMargem>[] = [
  { key: "vinculo", header: "Folha / matrícula", mono: true, render: (x) => `${x.folha} / ${x.matricula}` },
  { key: "produto", header: "Produto" },
  {
    key: "valor",
    header: "Mensal / aumento",
    render: (x) => `${fmtBRL(x.valorMensalCentavos / 100)} / ${fmtBRL(x.aumentoCentavos / 100)}`,
  },
  {
    key: "estado",
    header: "Estado",
    render: (x) => <Pill variant={x.estado === "divergente" ? "rejeitada" : "pendente"}>{x.estado}</Pill>,
  },
  { key: "contrato", header: "Contrato", mono: true, render: (x) => x.contratoAdf ?? "—" },
];
const colunasBloqueios: Column<BloqueioMargem>[] = [
  { key: "vinculo", header: "Folha / matrícula", mono: true, render: (x) => `${x.folha} / ${x.matricula}` },
  { key: "origem", header: "Origem" },
  {
    key: "vigencia",
    header: "Vigência",
    mono: true,
    render: (x) => `${x.competenciaInicio} — ${x.competenciaFim ?? "…"}`,
  },
  { key: "motivo", header: "Motivo", wrap: true },
  {
    key: "ativa",
    header: "Estado",
    render: (x) => (
      <Pill variant={x.ativa ? "rejeitada" : "expirado"}>{x.ativa ? "Ativo" : "Encerrado"}</Pill>
    ),
  },
];
/** Rótulo legível do tipo. O que não está aqui aparece como veio. */
const ROTULO_DIVERGENCIA: Record<string, string> = {
  arquivo_consignataria_pendente: "Arquivo da consignatária pendente",
  arquivo_consignataria_ausente: "Arquivo da consignatária ausente",
  suspensa_por_teto: "Cortado pelo teto global",
  corte_ignorado_pela_folha: "Folha descontou o que o teto cortou",
  manual_sobrescrita_por_arquivo: "Operação manual sobrescrita pelo arquivo",
  autorizacao_servidor_expirada: "Autorização do servidor expirada",
  autorizacao_servidor_cancelada: "Órgão deixou de exigir a autorização: inclusão não entra",
};

/** O tipo da divergência, com o grau e o caminho para resolver (#278). */
export function TipoDaDivergencia({ x }: { x: DivergenciaMargem }) {
  const tipo = x.tipo ?? x.acao;
  return (
    <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
      <span>{(tipo && ROTULO_DIVERGENCIA[tipo]) ?? tipo ?? "Conciliação"}</span>
      {x.severidade === "critico" ? <Pill variant="rejeitada">Crítico</Pill> : null}
      {/* O caminho para resolver é enviar o arquivo. */}
      {tipo === "arquivo_consignataria_pendente" && x.estado === "aberta" ? (
        <Link to="/averbadora/consignatarias/arquivos" style={{ fontSize: 12 }}>
          Enviar arquivo
        </Link>
      ) : null}
    </div>
  );
}

const colunasDivergencias: Column<DivergenciaMargem>[] = [
  { key: "data", header: "Data", render: (x) => (x.ts ? new Date(x.ts).toLocaleString("pt-BR") : "—") },
  { key: "acao", header: "Tipo", render: (x) => <TipoDaDivergencia x={x} /> },
  { key: "detalhes", header: "Detalhes", wrap: true, render: (x) => x.detalhes ?? "—" },
  { key: "estado", header: "Estado", render: (x) => x.estado ?? "aberta" },
];
function Info({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div>
      <div style={{ color: "var(--text-dim)", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
        {rotulo}
      </div>
      <div style={{ marginTop: 4, fontSize: 13 }}>{children}</div>
    </div>
  );
}
function Aviso({ children, erro = false }: { children: ReactNode; erro?: boolean }) {
  return (
    <div
      role={erro ? "alert" : "status"}
      style={{
        border: `1px solid ${erro ? "var(--danger-500)" : "var(--border)"}`,
        borderRadius: 8,
        padding: "10px 12px",
        marginTop: 12,
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
/**
 * Categoria de cada folha funcional, como a instituição declarou. Folha que
 * aparece na foto sem categoria impede ativar: ninguém decidiu se ela contrata.
 */
function CategoriasFolhaEditor({
  valor,
  onChange,
}: {
  valor: CategoriaFolhaMargem[];
  onChange: (v: CategoriaFolhaMargem[]) => void;
}) {
  const alterar = (i: number, patch: Partial<CategoriaFolhaMargem>) =>
    onChange(valor.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  return (
    <fieldset style={{ border: "1px solid var(--border)", borderRadius: 8, display: "grid", gap: 10 }}>
      <legend>Categorias das folhas</legend>
      {valor.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--text-dim)" }}>
          Nenhuma folha cadastrada. Toda folha presente na foto precisa de uma categoria para ativar.
        </div>
      ) : null}
      {valor.map((c, i) => (
        <div key={i} style={{ display: "grid", gap: 8, borderBottom: "1px solid var(--border)", paddingBottom: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr", gap: 8 }}>
            <TextField label="Folha" value={c.folha} onChange={(e) => alterar(i, { folha: e.target.value })} />
            <TextField label="Categoria" value={c.categoria} onChange={(e) => alterar(i, { categoria: e.target.value })} />
            <TextField
              label="Prazo máximo (meses)"
              type="number"
              value={c.prazoMaxMeses == null ? "" : String(c.prazoMaxMeses)}
              onChange={(e) => alterar(i, { prazoMaxMeses: e.target.value ? Math.max(1, Number(e.target.value)) : null })}
              hint="Carência inclusa; vazio = sem limite"
            />
          </div>
          <Check label="Elegível para nova consignação" checked={c.elegivel} onChange={(v) => alterar(i, { elegivel: v })} />
          <TextField label="Fundamento" value={c.fundamento} onChange={(e) => alterar(i, { fundamento: e.target.value })} />
          <TextField
            label="Mensagem de recusa por prazo"
            value={c.mensagemRecusa ?? ""}
            onChange={(e) => alterar(i, { mensagemRecusa: e.target.value || null })}
            hint="Opcional"
          />
          <div>
            <Button variant="ghost" onClick={() => onChange(valor.filter((_, j) => j !== i))}>
              Remover folha {c.folha}
            </Button>
          </div>
        </div>
      ))}
      <div>
        <Button
          variant="ghost"
          onClick={() =>
            onChange([...valor, { folha: "", categoria: "", elegivel: true, prazoMaxMeses: null, mensagemRecusa: null, fundamento: "" }])
          }
        >
          Adicionar folha
        </Button>
      </div>
    </fieldset>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
